import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { EVENTS_STRINGS } from '../constants/events-strings';
import { guestRegistrationErrorMessage } from '../guest-registration-error';
import { guestRegistrationSchema, type GuestRegistrationFormValues } from '../guest-registration-schema';
import { useGuestRegistration } from '../hooks/useGuestRegistration';

const S = EVENTS_STRINGS.GUEST;
const FORM_ID = 'guest-registration-form';

interface GuestRegistrationModalProps {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the visitor dismisses the modal after a successful registration. */
  onRegistered: () => void;
}

/**
 * "Register for this event" modal for a visitor without an account (FR-282,
 * FR-283). Full name and Email only. Composes the shared `Dialog`.
 *
 * Success swaps the form for a confirmation naming the address. The remembered
 * state is saved by the mutation, but the page only learns of it when the
 * visitor closes the modal, so the confirmation is never unmounted from under
 * them. It cannot be closed while the request is in flight. A failure is shown inline and leaves what they typed in place, so a 429
 * is retried without retyping. The email and name are never logged.
 */
export function GuestRegistrationModal({ eventId, open, onOpenChange, onRegistered }: GuestRegistrationModalProps) {
  const registration = useGuestRegistration(eventId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GuestRegistrationFormValues>({
    resolver: zodResolver(guestRegistrationSchema),
    defaultValues: { fullName: '', email: '' },
  });

  const onSubmit = handleSubmit(({ fullName, email }) => {
    registration.mutate({ fullName, email });
  });

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    // Closing mid-request would drop the answer: the registration still succeeds and
    // is remembered, but the visitor never sees the confirmation. Esc, the overlay
    // and the X all land here, so one guard covers them.
    if (registration.isPending) return;
    // Any close after a success (Done, X, Esc, overlay) tells the page.
    const wasRegistered = registration.isSuccess;
    reset();
    registration.reset();
    onOpenChange(false);
    if (wasRegistered) onRegistered();
  };

  if (registration.isSuccess) {
    return (
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        title={S.SUCCESS_TITLE}
        description={S.SUCCESS_BODY(registration.variables.email.trim())}
        footer={
          <Button variant="green" onClick={() => handleOpenChange(false)}>
            {S.DONE}
          </Button>
        }
      />
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={S.MODAL_TITLE}
      description={S.MODAL_DESCRIPTION}
      showCloseButton={!registration.isPending}
      footer={
        <Button type="submit" form={FORM_ID} variant="green" disabled={registration.isPending}>
          {registration.isPending ? S.SUBMITTING : S.SUBMIT}
        </Button>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} noValidate className="flex flex-col gap-3">
        <Input
          label={S.FULL_NAME_LABEL}
          showLabel
          autoComplete="name"
          placeholder={S.FULL_NAME_PLACEHOLDER}
          {...register('fullName')}
          error={errors.fullName?.message}
        />
        <Input
          label={S.EMAIL_LABEL}
          showLabel
          type="email"
          autoComplete="email"
          placeholder={S.EMAIL_PLACEHOLDER}
          {...register('email')}
          error={errors.email?.message}
        />
        {registration.isError && (
          <p role="alert" className="text-sm text-red">
            {guestRegistrationErrorMessage(registration.error)}
          </p>
        )}
      </form>
    </Dialog>
  );
}
